import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useCreateEmpregado, type CreateEmpregadoInput } from '@/lib/hooks/empregados';

interface FormValues {
  cpf: string;
  nome: string;
  cargo: string;
  funcao: string;
  cargaHorariaSemanal: string;
  salarioBase: string;
  dataAdmissao: string;
  naturezaContratacao: string;
  formacao: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULTS: FormValues = {
  cpf: '',
  nome: '',
  cargo: '',
  funcao: '',
  cargaHorariaSemanal: '',
  salarioBase: '',
  dataAdmissao: '',
  naturezaContratacao: '',
  formacao: '',
};

export function EmpregadoDrawer({ workspaceId, open, onOpenChange }: Props) {
  const createMut = useCreateEmpregado(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) reset(DEFAULTS);
  }, [open, reset]);

  async function onSubmit(values: FormValues) {
    const payload: CreateEmpregadoInput = {
      cpf: values.cpf.replace(/\D/g, ''),
      nome: values.nome,
    };
    if (values.cargo) payload.cargo = values.cargo;
    if (values.funcao) payload.funcao = values.funcao;
    if (values.cargaHorariaSemanal) payload.cargaHorariaSemanal = Number(values.cargaHorariaSemanal);
    if (values.salarioBase) payload.salarioBase = values.salarioBase.replace(',', '.');
    if (values.dataAdmissao) payload.dataAdmissao = values.dataAdmissao;
    if (values.naturezaContratacao) payload.naturezaContratacao = values.naturezaContratacao;
    if (values.formacao) payload.formacao = values.formacao;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Empregado cadastrado.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar empregado.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar empregado"
            description="Vinculado ao convênio para fins de prestação de contas"
          />
          <SheetBody className="space-y-5">
            <div>
              <Label htmlFor="nome" className="mb-1.5 block">Nome completo</Label>
              <Input
                id="nome"
                placeholder="Ex.: Maria das Graças Silva"
                {...register('nome', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.nome && <p className="text-[11px] text-warn mt-1">{errors.nome.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cpf" className="mb-1.5 block">CPF</Label>
                <Input
                  id="cpf"
                  placeholder="000.000.000-00"
                  {...register('cpf', {
                    required: 'Obrigatório',
                    validate: (v) => v.replace(/\D/g, '').length === 11 || 'CPF deve ter 11 dígitos',
                  })}
                />
                {errors.cpf && <p className="text-[11px] text-warn mt-1">{errors.cpf.message}</p>}
              </div>
              <div>
                <Label htmlFor="dataAdmissao" className="mb-1.5 block">Data de admissão</Label>
                <Input id="dataAdmissao" type="date" {...register('dataAdmissao')} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cargo" className="mb-1.5 block">Cargo</Label>
                <Input id="cargo" placeholder="Ex.: Professor" {...register('cargo')} />
              </div>
              <div>
                <Label htmlFor="funcao" className="mb-1.5 block">Função</Label>
                <Input id="funcao" placeholder="Ex.: Coordenação" {...register('funcao')} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="salarioBase" className="mb-1.5 block">Salário base (R$)</Label>
                <Input
                  id="salarioBase"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('salarioBase', {
                    pattern: { value: /^$|^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                  })}
                />
                {errors.salarioBase && (
                  <p className="text-[11px] text-warn mt-1">{errors.salarioBase.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="cargaHorariaSemanal" className="mb-1.5 block">Carga (h/semana)</Label>
                <Input
                  id="cargaHorariaSemanal"
                  type="number"
                  min="0"
                  max="80"
                  placeholder="40"
                  {...register('cargaHorariaSemanal')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="naturezaContratacao" className="mb-1.5 block">Natureza</Label>
                <Input
                  id="naturezaContratacao"
                  placeholder="Ex.: CLT, RPA, MEI"
                  {...register('naturezaContratacao')}
                />
              </div>
              <div>
                <Label htmlFor="formacao" className="mb-1.5 block">Formação</Label>
                <Input
                  id="formacao"
                  placeholder="Ex.: Pedagogia"
                  {...register('formacao')}
                />
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar empregado'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
